import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminApi } from '../api'
import {
  adminFixtureTimeline,
  useAdminFixturesFromQuery,
} from '../fixtures'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { ResponsiveTable } from '../../shared/ResponsiveTable'
import { Button } from '../../components/ui/Button'
import {
  eventDescription,
  eventTimestamp,
  formatWhen,
  maskLogin,
  safeAdminError,
} from '../safe-ui'

type AuditRow = {
  key: string
  timestamp: string
  event_type: string
  actor: string
  clientId: number
  clientLabel: string
  accountRef: string
  description: string
  result: string
}

export function AdminAuditPage() {
  const fixtures = useAdminFixturesFromQuery()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [rows, setRows] = useState<AuditRow[]>([])
  const [eventType, setEventType] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        if (fixtures) {
          const mapped = adminFixtureTimeline.map((ev, i) => {
            const rec = ev as Record<string, unknown>
            return {
              key: `fx-${i}`,
              timestamp: eventTimestamp(rec),
              event_type: String(rec.event_type || 'Event'),
              actor: 'system',
              clientId: 101,
              clientLabel: 'Asha Verma',
              accountRef: '—',
              description: eventDescription(rec),
              result: 'OK',
            }
          })
          if (!cancelled) setRows(mapped)
          return
        }

        const clients = await adminApi.listClients({
          page: 1,
          pageSize: 10,
          sort: 'id',
          order: 'desc',
        })
        const batches = await Promise.all(
          (clients.clients || []).slice(0, 10).map(async (c) => {
            try {
              const t = await adminApi.clientTimeline(c.id)
              return (t.events || []).map((ev, i) => {
                const accountId = ev.account_id != null ? `Acct #${ev.account_id}` : ''
                const login =
                  ev.mt5_login_masked != null ? maskLogin(String(ev.mt5_login_masked)) : ''
                const accountRef = [accountId, login].filter(Boolean).join(' · ') || '—'
                const meta = (ev.metadata || {}) as Record<string, unknown>
                const actorType = String(ev.actor_type || '')
                const actorId = ev.actor_id != null ? String(ev.actor_id) : ''
                const actor = actorType
                  ? `${actorType}${actorId ? ` #${actorId}` : ''}`
                  : meta.admin_id
                    ? `admin #${meta.admin_id}`
                    : 'system'
                const success = meta.success
                const result =
                  success === false || success === 0
                    ? 'Failed'
                    : success === true || success === 1
                      ? 'OK'
                      : '—'
                return {
                  key: `${c.id}-${i}-${eventTimestamp(ev)}`,
                  timestamp: eventTimestamp(ev),
                  event_type: String(ev.event_type || 'Event'),
                  actor,
                  clientId: c.id,
                  clientLabel: c.display_name || c.email || `#${c.id}`,
                  accountRef,
                  description: eventDescription(ev),
                  result,
                } satisfies AuditRow
              })
            } catch {
              return [] as AuditRow[]
            }
          }),
        )

        const merged = batches
          .flat()
          .sort((a, b) => {
            const ta = new Date(a.timestamp).getTime() || 0
            const tb = new Date(b.timestamp).getTime() || 0
            return tb - ta
          })

        if (!cancelled) setRows(merged)
      } catch (err) {
        if (!cancelled) setError(safeAdminError(err, 'Unable to load audit events'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [fixtures])

  const filtered = useMemo(() => {
    let list = rows
    if (eventType) {
      const q = eventType.toLowerCase()
      list = list.filter((r) => r.event_type.toLowerCase().includes(q))
    }
    if (dateFrom) {
      const start = new Date(dateFrom).getTime()
      if (!Number.isNaN(start)) {
        list = list.filter((r) => {
          const t = new Date(r.timestamp).getTime()
          return Number.isNaN(t) ? true : t >= start
        })
      }
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter(
        (r) =>
          r.description.toLowerCase().includes(q) ||
          r.clientLabel.toLowerCase().includes(q) ||
          r.event_type.toLowerCase().includes(q) ||
          r.actor.toLowerCase().includes(q) ||
          r.accountRef.toLowerCase().includes(q),
      )
    }
    return list
  }, [rows, eventType, dateFrom, search])

  const q = fixtures ? '?fixtures=1' : ''

  return (
    <section className="cp-page">
      <PageHeader eyebrow="Audit" title="Audit trail">
        <p className="cp-muted">
          Aggregated from client timelines (first page of clients). No standalone global audit API.
        </p>
      </PageHeader>

      {fixtures ? (
        <StateBlock tone="warn" title="Fixture mode · visual review only">
          Preview timeline events only.
        </StateBlock>
      ) : (
        <StateBlock tone="info" title="Honest limitation">
          There is no global `/api/admin/audit` list. Events below are merged from up to 10 recent client timelines.
        </StateBlock>
      )}

      <div className="ap-filters">
        <label className="cp-field">
          Event type
          <input
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            placeholder="e.g. CLIENT_SUSPENDED"
          />
        </label>
        <label className="cp-field">
          From date
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className="cp-field">
          Search
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="description, client, actor"
          />
        </label>
        <Button
          variant="ghost"
          onClick={() => {
            setEventType('')
            setDateFrom('')
            setSearch('')
          }}
        >
          Clear
        </Button>
      </div>

      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading…
        </p>
      ) : null}
      {error ? <p className="cp-error">{error}</p> : null}

      <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
        <ResponsiveTable
          rows={filtered}
          rowKey={(r) => r.key}
          empty={loading ? 'Loading…' : 'No audit events for this filter.'}
            columns={[
              {
                key: 'ts',
                header: 'Timestamp',
                render: (r) => formatWhen(r.timestamp),
              },
              { key: 'type', header: 'Event type', render: (r) => r.event_type },
              { key: 'actor', header: 'Actor', render: (r) => r.actor },
              {
                key: 'client',
                header: 'Client',
                render: (r) => (
                  <Link to={`/admin/clients/${r.clientId}${q}`}>{r.clientLabel}</Link>
                ),
              },
              { key: 'ref', header: 'Account/ref', render: (r) => r.accountRef },
              { key: 'desc', header: 'Description', render: (r) => r.description || '—' },
              { key: 'result', header: 'Result', render: (r) => r.result },
            ]}
          />
      </div>
    </section>
  )
}
